import type { HeroVersionId } from './versions.ts'

/**
 * Hero A/B tests on `/`, oldest first. At most one runs at a time; a visitor without `?v=` gets
 * one of its arms at random (weights), sticky per browser. The arm is recorded as
 * `<test>.<version>` (e.g. `t1.h3`) in `leads.variant`, `page_views.variant`, every server event
 * and PostHog, so tests never mix even when they reuse a version. Never change arms or weights of
 * a running test: end it and start a new one. Workflow: docs/HERO-AB.md.
 */

export type HeroTestStatus = 'draft' | 'running' | 'ended'

export type HeroTest = {
  id: string
  name: string
  hypothesis: string
  status: HeroTestStatus
  /** Europe/Warsaw dates, inclusive; the admin panel reads results over this window. */
  start?: string
  end?: string
  /** The first arm is the control. */
  arms: ReadonlyArray<{ version: HeroVersionId; weight: number }>
  /** Filled in when the test ends: the version that became the default and why. */
  decision?: string
}

export const HERO_TESTS: ReadonlyArray<HeroTest> = [
  {
    id: 't1',
    name: 'Hook New vs Wycena',
    hypothesis:
      'Krótkie hero z obietnicą wyceny (h3) daje więcej rezerwacji na wejście niż Hook New z terminem i krokami (h2).',
    status: 'running',
    start: '2026-11-02',
    arms: [
      { version: 'h2', weight: 1 },
      { version: 'h3', weight: 1 },
    ],
  },
]

/** Shown when no test runs and to visitors outside a test (unknown `?v=`, e.g. a campaign tag). */
export const DEFAULT_HERO: HeroVersionId = 'h2'

export const armVariant = (test: Pick<HeroTest, 'id'>, version: string) => `${test.id}.${version}`

export function runningTest(tests: ReadonlyArray<HeroTest> = HERO_TESTS): HeroTest | undefined {
  return tests.find((t) => t.status === 'running')
}
